import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastService } from '@sortaify/client-core';
import { animate, style, transition, trigger } from '@angular/animations';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-toast',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, MatButtonModule],
  template: `
    <div class="fixed bottom-4 right-4 z-50 flex flex-col gap-3 pointer-events-none">
      @for (toast of toastService.toasts(); track toast.id) {
        <div
          [@toastAnimation]
          class="pointer-events-auto flex items-start gap-3 w-80 p-4 rounded-xl shadow-xl border bg-slate-900/95 backdrop-blur-md transition-all {{ toast.type === 'success' ? 'border-emerald-500/50 shadow-emerald-500/10' : toast.type === 'error' ? 'border-red-500/50 shadow-red-500/10' : 'border-violet-600/50 shadow-violet-600/10' }}"
        >
          <!-- Icons -->
          <div class="mt-0.5">
            @if (toast.type === 'success') {
              <mat-icon class="text-emerald-400">check_circle</mat-icon>
            }
            @if (toast.type === 'error') {
              <mat-icon class="text-red-400">cancel</mat-icon>
            }
            @if (toast.type === 'info') {
              <mat-icon class="text-violet-400">info</mat-icon>
            }
          </div>

          <!-- Content -->
          <div class="flex-1 min-w-0 pt-0.5">
            @if (toast.title) {
              <h4 class="text-sm font-semibold mb-0.5" 
                  [class.text-emerald-300]="toast.type === 'success'"
                  [class.text-red-300]="toast.type === 'error'"
                  [class.text-indigo-300]="toast.type === 'info'"
              >
                {{ toast.title }}
              </h4>
            }
            <p class="text-sm text-slate-300 leading-snug">{{ toast.message }}</p>
          </div>

          <!-- Close button -->
          <button
            mat-icon-button
            class="ml-1 text-slate-500 hover:text-slate-300"
            (click)="toastService.remove(toast.id)"
            aria-label="Close notification"
          >
            <mat-icon class="text-base">close</mat-icon>
          </button>
        </div>
      }
    </div>
  `,
  animations: [
    trigger('toastAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px) scale(0.9)' }),
        animate('300ms cubic-bezier(0.175, 0.885, 0.32, 1.275)', style({ opacity: 1, transform: 'translateY(0) scale(1)' })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'scale(0.9)' })),
      ]),
    ]),
  ],
})
export class ToastComponent {
  toastService = inject(ToastService);
}
